import { createResponse } from "../lib/functions";
import { AirportDestination } from "../types/airportDestination";
import golApiCall from "./golApiCall";

export const MAX_NEARBY_HOTEL_DESTINATIONS = 5;
export const NEARBY_HOTEL_DESTINATION_REQUEST_LIMIT =
  MAX_NEARBY_HOTEL_DESTINATIONS + 1;

export type DestinationCoordinates = {
  lat: number;
  lng: number;
};

export type HotelDestination = AirportDestination & {
  Category?: string;
  Coordinates?: DestinationCoordinates;
};

export type NearbyHotelDestination = HotelDestination & {
  Coordinates: DestinationCoordinates;
  DistanceFromCoordinatesInKm: number;
};

const asArray = <T>(value: T | T[] | undefined): T[] => {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

const parseCoordinate = (value: unknown): number | null => {
  if (typeof value === "string" && value.trim() === "") return null;

  const coordinate =
    typeof value === "number" || typeof value === "string"
      ? Number(value)
      : NaN;

  return Number.isFinite(coordinate) ? coordinate : null;
};

const parseShowCode = (value: unknown): boolean =>
  value === undefined ? true : value === true || value === "true";

export function getDestinationCoordinates(
  destination: Record<string, any> | null | undefined
): DestinationCoordinates | null {
  if (!destination) return null;

  const coordinateSource = destination.Coordinates || destination;
  const lat = parseCoordinate(
    coordinateSource.Latitude ??
      coordinateSource.latitude ??
      coordinateSource.lat
  );
  const lng = parseCoordinate(
    coordinateSource.Longitude ??
      coordinateSource.longitude ??
      coordinateSource.lng
  );

  return lat !== null &&
    lng !== null &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
    ? { lat, lng }
    : null;
}

export function findDestinationInCodebook(
  codebook: Record<string, any> | null | undefined,
  destinationCode: string
): Record<string, any> | null {
  if (!codebook || !destinationCode) return null;

  return (
    Object.values(codebook)
      .filter((section) => section && typeof section === "object")
      .flatMap((section) =>
        Object.values(section as Record<string, any>).flatMap((entries) =>
          asArray(entries as Record<string, any>)
        )
      )
      .find((destination) => destination?.Code === destinationCode) || null
  );
}

export function getGolRequestDestinationSearchQuery(
  searchDestinationPattern: string,
  searchType?: string
): Record<string, any> {
  return {
    GolApi: {
      RequestDetail: {
        SearchDestinationsRequest_1: {
          SearchPattern: {
            $t: searchDestinationPattern,
            SearchType: searchType || "flight",
          },
        },
      },
    },
  };
}

export function getGolRequestNearestHotelDestinationsQuery({
  country,
  latitude,
  limit = NEARBY_HOTEL_DESTINATION_REQUEST_LIMIT,
  longitude,
}: {
  country: string;
  latitude: number;
  limit?: number;
  longitude: number;
}): Record<string, any> {
  return {
    GolApi: {
      RequestDetail: {
        SearchDestinationsRequest_1: {
          SearchNearest: {
            SearchType: "hotel",
            Country: country,
            Latitude: latitude,
            Longitude: longitude,
            Limit: limit,
          },
        },
      },
    },
  };
}

function mapSearchResultsForAutosuggest(searchDestinationResponse, codebook) {
  return asArray(
    searchDestinationResponse?.SearchedAirports?.SearchedAirport
  ).flatMap((searchedDestination: any) => {
    const destinationCodebook = findDestinationInCodebook(
      codebook,
      searchedDestination.Destination
    );

    if (!destinationCodebook) return [];

    return [
      {
        Code: searchedDestination.Destination,
        Parent: searchedDestination.Parent,
        Country: destinationCodebook.Country,
        State: destinationCodebook.State,
        $t: destinationCodebook.$t,
        Category: destinationCodebook.Category,
        ShowCode: parseShowCode(searchedDestination.ShowCode),
        Coordinates:
          getDestinationCoordinates(searchedDestination) ||
          getDestinationCoordinates(destinationCodebook) ||
          undefined,
      },
    ];
  });
}

function mapNearestHotelDestinations(searchDestinationResponse, codebook) {
  return asArray(
    searchDestinationResponse?.SearchedAirports?.SearchedAirport
  ).flatMap((searchedDestination: any) => {
    const destinationCodebook = findDestinationInCodebook(
      codebook,
      searchedDestination.Destination
    );
    const coordinates =
      getDestinationCoordinates(searchedDestination) ||
      getDestinationCoordinates(destinationCodebook);
    const distance = Number(searchedDestination.DistanceFromCoordinatesInKm);

    if (
      !destinationCodebook ||
      !coordinates ||
      !Number.isFinite(distance) ||
      distance < 0
    ) {
      return [];
    }

    return [
      {
        Code: searchedDestination.Destination,
        Parent: searchedDestination.Parent,
        Country: destinationCodebook.Country,
        State: destinationCodebook.State,
        $t: destinationCodebook.$t,
        Category: destinationCodebook.Category,
        ShowCode: parseShowCode(searchedDestination.ShowCode),
        Coordinates: coordinates,
        DistanceFromCoordinatesInKm: distance,
      },
    ];
  });
}

export async function searchDestinations(
  searchDestinationPattern: string,
  searchType?: string
): Promise<HotelDestination[]> {
  const searchDestinationResponse = await golApiCall(
    getGolRequestDestinationSearchQuery(searchDestinationPattern, searchType)
  );
  const response = createResponse(searchDestinationResponse);

  if (!response.success) return [];

  return mapSearchResultsForAutosuggest(response.data, response.codebook);
}

export async function searchNearestHotelDestinations({
  country,
  destinationCode,
  latitude,
  limit = NEARBY_HOTEL_DESTINATION_REQUEST_LIMIT,
  longitude,
}: {
  country: string;
  destinationCode: string;
  latitude: number;
  limit?: number;
  longitude: number;
}): Promise<NearbyHotelDestination[]> {
  const normalizedCountry = country?.trim().toUpperCase();
  const normalizedDestinationCode = destinationCode?.trim().toUpperCase();
  const coordinates = getDestinationCoordinates({
    lat: latitude,
    lng: longitude,
  });

  if (!normalizedCountry || !normalizedDestinationCode || !coordinates) {
    return [];
  }

  const requestedLimit = Number.isFinite(limit)
    ? Math.min(Math.max(Math.trunc(limit), 1), 100)
    : NEARBY_HOTEL_DESTINATION_REQUEST_LIMIT;
  const searchDestinationResponse = await golApiCall(
    getGolRequestNearestHotelDestinationsQuery({
      country: normalizedCountry,
      latitude: coordinates.lat,
      limit: requestedLimit,
      longitude: coordinates.lng,
    })
  );
  const response = createResponse(searchDestinationResponse);

  if (!response.success) return [];

  const seenDestinationCodes = new Set<string>();

  return mapNearestHotelDestinations(response.data, response.codebook)
    .filter(
      (destination) =>
        destination.Code?.trim().toUpperCase() !== normalizedDestinationCode &&
        destination.Country?.trim().toUpperCase() === normalizedCountry &&
        destination.Category === "CITY"
    )
    .sort(
      (first, second) =>
        first.DistanceFromCoordinatesInKm - second.DistanceFromCoordinatesInKm
    )
    .filter((destination) => {
      const normalizedCode = destination.Code.trim().toUpperCase();
      if (seenDestinationCodes.has(normalizedCode)) return false;

      seenDestinationCodes.add(normalizedCode);
      return true;
    })
    .slice(0, MAX_NEARBY_HOTEL_DESTINATIONS);
}

export default searchDestinations;
